import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Box, Button, Chip, IconButton, Paper, Tab, Tabs, Tooltip, Typography } from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import TodayIcon from '@mui/icons-material/Today'
import EventBusyIcon from '@mui/icons-material/EventBusyOutlined'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import AddIcon from '@mui/icons-material/Add'
import BlockIcon from '@mui/icons-material/BlockOutlined'
import HistoryIcon from '@mui/icons-material/HistoryOutlined'
import { useLiveQuery } from 'dexie-react-hooks'
import {
  aujourdhuiBoutique,
  enHeure,
  enMinutes,
  horairesParDefaut,
  LIBELLES_STATUT_RDV,
  instantDepuisHeureMurale,
  minutesMurales,
  placesFermees,
  plancherDuJour,
  postesBloques,
  type BlocageDuJour,
  type Category,
  type RendezVous,
} from '@iobiz/shared'
import { useSessionStore } from '../stores/sessionStore.js'
import {
  annulerRendezVous,
  blocagesLocaux,
  rendezVousLocaux,
  validerRendezVous,
  type BlocageLocal,
} from '../data/repositories/agenda.local.js'
import { categoriesLocales } from '../data/repositories/catalog.local.js'
import { BlocagesDialog } from '../features/agenda/BlocagesDialog.js'
import { PriseRendezVousDialog } from '../features/agenda/PriseRendezVousDialog.js'
import { DeplacerRendezVousDialog } from '../features/agenda/DeplacerRendezVousDialog.js'
import { ConfirmDialog } from '../ui/ConfirmDialog.js'
import { versIso } from '../ui/ChampDate.js'

/**
 * L'agenda des prestations.
 *
 * ⭐ Trois échelles, parce qu'on ne cherche pas la même chose : la JOURNÉE pour
 * travailler (qui vient, à quelle heure, quel créneau reste), le MOIS pour
 * répondre « quand êtes-vous libre ? » devant un client, l'ANNÉE pour voir les
 * creux et les pleins — la saison des mariages se repère d'un coup d'œil, pas
 * en feuilletant douze mois.
 *
 * ⚠️ Tout est lu dans la RÉPLIQUE locale. Un agenda qui exige le réseau est
 * inutilisable précisément au moment où l'on en a besoin : devant le comptoir,
 * client en face.
 */

type Vue = 'jour' | 'mois' | 'annee'

const MOIS_FR = [
  'janvier',
  'février',
  'mars',
  'avril',
  'mai',
  'juin',
  'juillet',
  'août',
  'septembre',
  'octobre',
  'novembre',
  'décembre',
]

/** Lundi en tête : l'ordre de lecture, pas l'ordre de stockage. */
const ENTETES_SEMAINE = ['L', 'M', 'M', 'J', 'V', 'S', 'D']

function debutDeJournee(d: Date): Date {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  return x
}

function ajouterJours(d: Date, n: number): Date {
  const x = new Date(d)
  x.setDate(x.getDate() + n)
  return x
}

/**
 * ⚠️ `getDay()` rend 0 pour dimanche. Une grille qui commence le lundi doit
 * donc décaler de 6, pas de 1 : c'est l'erreur d'un cran qui ne se voit qu'une
 * fois par semaine, le dimanche, et qui décale tout le mois.
 */
function colonneLundiEnTete(d: Date): number {
  return (d.getDay() + 6) % 7
}

export function AgendaPage(): JSX.Element {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const store = useSessionStore((s) => s.stores.find((b) => b.slug === slug))
  const storeId = store?.storeId

  // ⚠️ `?jour=` sert les liens venus d'ailleurs — le journal des ventes, par
  // exemple. Sans lui, cliquer « 14 sept. » depuis une vente ouvrait l'agenda
  // sur aujourd'hui, c'est-à-dire à côté de ce qu'on venait voir.
  const [params] = useSearchParams()
  const [vue, setVue] = useState<Vue>('jour')
  const [curseur, setCurseur] = useState(() => {
    const demande = params.get('jour')
    // Midi : construire la date à minuit la ferait basculer la veille à
    // l'ouest de Greenwich.
    return demande ? debutDeJournee(new Date(`${demande}T12:00:00`)) : debutDeJournee(new Date())
  })
  const [prise, setPrise] = useState<{
    jour: Date
    posteId?: string
    categorieId?: string
    /** L'heure de la case cliquée, « 10:30 ». */
    heure?: string
  } | null>(null)
  const [blocagesOuverts, setBlocagesOuverts] = useState(false)
  const [aDeplacer, setADeplacer] = useState<RendezVous | null>(null)
  const [aAnnuler, setAAnnuler] = useState<RendezVous | null>(null)

  const horaires = store?.openingHours ?? horairesParDefaut()
  const fuseau = store?.timezone ?? 'Africa/Douala'

  /**
   * ⭐ L'horloge AVANCE : une page laissée ouverte au comptoir toute la
   * journée doit griser ses créneaux à mesure qu'ils passent, sans qu'on la
   * recharge. Une minute suffit — c'est le grain le plus fin de la grille.
   */
  const [, battre] = useState(0)
  useEffect(() => {
    const t = setInterval(() => battre((n) => n + 1), 60_000)
    return () => clearInterval(t)
  }, [])
  // Le jour qu'il est À LA BOUTIQUE : c'est lui qui départage passé et futur.
  const aujourdhui = aujourdhuiBoutique(fuseau)
  /**
   * Le jour où ouvrir la prise de rendez-vous.
   *
   * ⚠️ Jamais un jour passé : depuis la journée d'hier, « Nouveau
   * rendez-vous » ouvrait une modale sur hier, sans aucun créneau et sans rien
   * qui dise pourquoi. On la rouvre sur aujourd'hui.
   */
  const jourDePrise = (j: Date): Date =>
    versIso(j) < aujourdhui ? new Date(`${aujourdhui}T12:00:00`) : j

  /** La fenêtre à charger dépend de l'échelle : un jour, un mois, une année. */
  const [debut, fin] = useMemo((): [Date, Date] => {
    if (vue === 'jour') return [debutDeJournee(curseur), ajouterJours(debutDeJournee(curseur), 1)]
    if (vue === 'mois') {
      const d = new Date(curseur.getFullYear(), curseur.getMonth(), 1)
      return [d, new Date(curseur.getFullYear(), curseur.getMonth() + 1, 1)]
    }
    return [new Date(curseur.getFullYear(), 0, 1), new Date(curseur.getFullYear() + 1, 0, 1)]
  }, [vue, curseur])

  // ⭐ Les catégories dessinent l'agenda : une colonne par catégorie de
  // services, une ligne par poste. Lues dans la réplique, comme les
  // rendez-vous — le planning doit tenir sans réseau.
  const categories = useLiveQuery(
    () => (storeId ? categoriesLocales(storeId) : Promise.resolve([])),
    [storeId],
  )

  const rdvs = useLiveQuery(
    () =>
      storeId
        ? rendezVousLocaux(storeId, debut.toISOString(), fin.toISOString())
        : Promise.resolve([]),
    [storeId, debut.getTime(), fin.getTime()],
  )

  /**
   * ⭐ Les BLOCAGES, lus dans la réplique comme les rendez-vous. La grille doit
   * dire ce qui est fermé sans attendre le réseau : sinon, hors ligne, elle
   * proposerait des places que la synchronisation refuse ensuite — une promesse
   * faite au client, puis reprise.
   */
  const blocages = useLiveQuery(
    () =>
      storeId
        ? blocagesLocaux(storeId, debut.toISOString(), fin.toISOString())
        : Promise.resolve([]),
    [storeId, debut.getTime(), fin.getTime()],
  )

  /** Combien de rendez-vous par journée — sert au mois comme à l'année. */
  const parJour = useMemo(() => {
    const carte = new Map<string, RendezVous[]>()
    for (const r of rdvs ?? []) {
      const cle = new Date(r.startsAt).toDateString()
      carte.set(cle, [...(carte.get(cle) ?? []), r])
    }
    return carte
  }, [rdvs])

  function deplacer(sens: 1 | -1): void {
    if (vue === 'jour') setCurseur((c) => ajouterJours(c, sens))
    else if (vue === 'mois') setCurseur((c) => new Date(c.getFullYear(), c.getMonth() + sens, 1))
    else setCurseur((c) => new Date(c.getFullYear() + sens, c.getMonth(), 1))
  }

  const titre =
    vue === 'jour'
      ? curseur.toLocaleDateString('fr-FR', {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        })
      : vue === 'mois'
        ? `${MOIS_FR[curseur.getMonth()]} ${curseur.getFullYear()}`
        : String(curseur.getFullYear())

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 3,
          flexWrap: 'wrap',
          mb: 4,
        }}
      >
        <Typography variant="h1" sx={{ flex: 1, minWidth: 200, textTransform: 'capitalize' }}>
          {titre}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton aria-label="Précédent" onClick={() => deplacer(-1)}>
            <ChevronLeftIcon />
          </IconButton>
          <Tooltip title="Aujourd’hui">
            <IconButton
              aria-label="Aujourd’hui"
              onClick={() => setCurseur(debutDeJournee(new Date()))}
            >
              <TodayIcon />
            </IconButton>
          </Tooltip>
          <IconButton aria-label="Suivant" onClick={() => deplacer(1)}>
            <ChevronRightIcon />
          </IconButton>
        </Box>

        <Tabs value={vue} onChange={(_, v: Vue) => setVue(v)}>
          <Tab value="jour" label="Jour" />
          <Tab value="mois" label="Mois" />
          <Tab value="annee" label="Année" />
        </Tabs>

        {/* ⭐ À côté de « Nouveau rendez-vous », parce que c'est le même
            geste de planning : on ouvre une place ou on en ferme une. */}
        <Button startIcon={<BlockIcon />} onClick={() => setBlocagesOuverts(true)}>
          Blocages
        </Button>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setPrise({ jour: jourDePrise(curseur) })}
        >
          Nouveau rendez-vous
        </Button>
      </Box>

      {vue === 'jour' && (
        <VueJour
          jour={curseur}
          rdvs={rdvs ?? []}
          blocages={blocages ?? []}
          categories={categories ?? []}
          horaires={horaires}
          fuseau={fuseau}
          onCreneau={(posteId, categorieId, heure) =>
            setPrise({
              jour: jourDePrise(curseur),
              ...(posteId ? { posteId } : {}),
              ...(categorieId ? { categorieId } : {}),
              ...(heure ? { heure } : {}),
            })
          }
          onOuvrir={(r) => r.transactionId && navigate(`/s/${slug}/ventes/${r.transactionId}`)}
          onDeplacer={setADeplacer}
          onAnnuler={setAAnnuler}
          onValider={(r) => storeId && void validerRendezVous(storeId, r)}
        />
      )}

      {vue === 'mois' && (
        <VueMois
          curseur={curseur}
          aujourdhui={aujourdhui}
          parJour={parJour}
          onJour={(j) => {
            setCurseur(j)
            setVue('jour')
          }}
        />
      )}

      {vue === 'annee' && (
        <VueAnnee
          annee={curseur.getFullYear()}
          aujourdhui={aujourdhui}
          parJour={parJour}
          onMois={(m) => {
            setCurseur(new Date(curseur.getFullYear(), m, 1))
            setVue('mois')
          }}
        />
      )}

      {aDeplacer && storeId && (
        <DeplacerRendezVousDialog
          storeId={storeId}
          rdv={aDeplacer}
          horaires={horaires}
          fuseau={fuseau}
          onClose={() => setADeplacer(null)}
        />
      )}

      {/* ⚠️ Une CONFIRMATION, pas un clic direct : annuler libère le créneau et
          prévient le client. Le geste est réversible en base — le rendez-vous
          reste, avec son statut — mais il ne l'est pas dans la tête du client
          qu'on aura appelé pour rien. */}
      <ConfirmDialog
        open={aAnnuler !== null}
        title="Annuler ce rendez-vous ?"
        message={
          aAnnuler
            ? `${aAnnuler.label} — ${aAnnuler.customerName ?? 'client'}. Le créneau redevient disponible ; le rendez-vous reste visible dans l'historique du client.`
            : ''
        }
        confirmLabel="Annuler le rendez-vous"
        onClose={() => setAAnnuler(null)}
        onConfirm={async () => {
          if (storeId && aAnnuler) await annulerRendezVous(storeId, aAnnuler)
          setAAnnuler(null)
        }}
      />

      {storeId && (
        <BlocagesDialog
          open={blocagesOuverts}
          storeId={storeId}
          categories={categories ?? []}
          fuseau={fuseau}
          pas={horaires.slotMinutes}
          onClose={() => setBlocagesOuverts(false)}
        />
      )}

      {prise && storeId && (
        <PriseRendezVousDialog
          storeId={storeId}
          jour={prise.jour}
          posteInitial={prise.posteId}
          categorieInitiale={prise.categorieId}
          heureInitiale={prise.heure}
          horaires={horaires}
          fuseau={fuseau}
          onClose={() => setPrise(null)}
        />
      )}
    </Box>
  )
}

// ────────────────────────────────────────────────────────────────── la journée

/**
 * La journée, en COLONNES.
 *
 * ⭐ Une colonne par catégorie de services, et dans chaque colonne une ligne
 * par POSTE : trois fauteuils de coiffure font trois lignes, deux tables de
 * soin en font deux. C'est la forme du planning réel — on ne cherche pas « la
 * boutique est-elle libre à 14 h » mais « quelle place reste-t-il à 14 h ».
 *
 * ⚠️ Les rendez-vous SANS poste — pris avant que les postes existent — ne
 * figurent PAS dans cette grille : elle est faite de postes, et ils n'en ont
 * aucun. Ils restent lisibles ailleurs (vues mois et année, fiche client,
 * vente), mais la journée ne montre que des places réelles.
 */
function VueJour({
  jour,
  rdvs,
  blocages,
  categories,
  horaires,
  fuseau,
  onCreneau,
  onOuvrir,
  onDeplacer,
  onAnnuler,
  onValider,
}: {
  jour: Date
  rdvs: RendezVous[]
  blocages: BlocageLocal[]
  categories: Category[]
  horaires: ReturnType<typeof horairesParDefaut>
  fuseau: string
  onCreneau: (posteId?: string, categorieId?: string, heure?: string) => void
  onOuvrir: (r: RendezVous) => void
  onDeplacer: (r: RendezVous) => void
  onAnnuler: (r: RendezVous) => void
  onValider: (r: RendezVous) => void
}): JSX.Element {
  const journee = horaires.days[jour.getDay()]

  /** Les colonnes : catégories de services pourvues d'au moins un poste. */
  const colonnes = categories.filter((c) => c.kind === 'service' && c.postes.length > 0)

  if (!journee || journee.closed) {
    return (
      <Paper variant="outlined" sx={{ p: 8, textAlign: 'center' }}>
        <Typography variant="h3" sx={{ mb: 2 }}>
          Boutique fermée
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Aucun créneau ce jour-là. Les jours d’ouverture se règlent dans Paramètres › Agenda.
        </Typography>
      </Paper>
    )
  }

  if (colonnes.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 8, textAlign: 'center' }}>
        <Typography variant="h3" sx={{ mb: 2 }}>
          Aucun poste
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          L’agenda se dessine à partir des postes de vos catégories de services. Créez-en dans
          Catalogue › Catégories, onglet Services.
        </Typography>
      </Paper>
    )
  }

  /**
   * ⚠️ L'heure PASSÉE est marquée comme telle, et non « libre ».
   *
   * La grille annonçait « Libre » à 9 h alors qu'il était 14 h, pendant que la
   * prise de rendez-vous refusait — à raison — de proposer cette heure-là. Deux
   * écrans de la même application se contredisaient, et c'est celui qui promet
   * une disponibilité inexistante qui avait tort.
   */
  // ⚠️ Le jour AFFICHÉ se lit en composantes locales — `jour` est un minuit du
  // poste — puis se juge à l'horloge de la BOUTIQUE. Comparer deux
  // `jourMural` d'instants décalait tout d'un jour quand le poste est à l'est
  // de la boutique (Paris devant Douala ou Bamako).
  const plancher = plancherDuJour(versIso(jour), fuseau)
  const journeePassee = plancher === Number.POSITIVE_INFINITY

  /**
   * Les créneaux, dessinés à partir des HORAIRES et non des rendez-vous : une
   * journée vide doit montrer ses places libres, sinon l'écran n'apprend rien —
   * alors que « voilà ce qui reste » est ce qu'on vient y chercher.
   */
  const minutes: number[] = []
  for (const plage of journee.ranges) {
    for (let m = enMinutes(plage.start); m < enMinutes(plage.end); m += horaires.slotMinutes) {
      minutes.push(m)
    }
  }

  /**
   * Les fermetures de la journée, par catégorie, RAMENÉES EN MINUTES MURALES.
   *
   * ⚠️ Un blocage traverse les jours — « du 23 au 30 ». On le rogne sur la
   * journée affichée avant de le convertir : sans cela, un blocage commencé la
   * veille donnerait une minute de départ négative, un blocage finissant le
   * lendemain une minute au-delà de 1440, et les deux sortiraient du calcul —
   * c'est-à-dire qu'une semaine de fermeture ne se verrait que le premier jour.
   *
   * ⭐ Même découpage que le serveur, pour que la grille du comptoir et les
   * disponibilités du site public ne puissent pas se contredire.
   */
  const fermeturesParCategorie = useMemo(() => {
    const debutJour = instantDepuisHeureMurale(versIso(jour), '00:00', fuseau)
    const finJour = new Date(new Date(debutJour).getTime() + 24 * 3600 * 1000).toISOString()
    const carte = new Map<string, (BlocageDuJour & { motif: string | null })[]>()
    for (const b of blocages) {
      if (!(b.debut < finJour && b.fin > debutJour)) continue
      const entree = {
        debut: b.debut <= debutJour ? 0 : minutesMurales(b.debut, fuseau),
        fin: b.fin >= finJour ? 24 * 60 : minutesMurales(b.fin, fuseau),
        postes: b.postes,
        motif: b.motif,
      }
      carte.set(b.categoryId, [...(carte.get(b.categoryId) ?? []), entree])
    }
    return carte
  }, [blocages, jour, fuseau])

  /** Qui occupe ce poste à cette minute — la question que pose chaque case. */
  function occupant(posteId: string, minute: number): RendezVous | undefined {
    return rdvs.find(
      (r) =>
        r.posteId === posteId &&
        r.status !== 'cancelled' &&
        minutesMurales(r.startsAt, fuseau) <= minute &&
        minutesMurales(r.endsAt, fuseau) > minute,
    )
  }

  /**
   * ⭐ UNE SOUS-COLONNE PAR POSTE, côte à côte — comme un agenda de bureau.
   *
   * Les postes étaient EMPILÉS dans la bande horaire. Deux fauteuils portant la
   * même prestation à la même heure donnaient alors deux cases identiques l'une
   * au-dessus de l'autre, et un rendez-vous d'une heure et demie se brisait en
   * trois morceaux séparés par la case du voisin.
   *
   * Côte à côte, chaque poste tient sa propre colonne de haut en bas : la durée
   * se lit comme une hauteur, et deux rendez-vous simultanés ne se confondent
   * plus puisqu'ils ne sont plus l'un sous l'autre.
   *
   * ⚠️ Une catégorie large de ses postes, pas d'une part égale : trois
   * fauteuils ont besoin de trois fois la place d'un seul. La grille déborde
   * alors en largeur — c'est le prix, et le `Paper` la fait défiler.
   */
  const LARGEUR_POSTE = 140
  const grille = `72px ${colonnes
    .map((c) => `minmax(${LARGEUR_POSTE * c.postes.length}px, ${c.postes.length}fr)`)
    .join(' ')}`

  /** Les sous-colonnes d'une catégorie : une par poste. */
  const sousColonnes = (c: Category): object => ({
    display: 'grid',
    gridTemplateColumns: `repeat(${c.postes.length}, 1fr)`,
  })

  return (
    // ⚠️ Le défilement horizontal vit ICI, pas sur la page : au-delà de trois
    // ou quatre catégories la grille dépasse l'écran, et c'est elle qui doit
    // glisser — pas toute l'application.
    <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
      {/* Une journée passée se CONSULTE — on y relit qui est venu — mais ne se
          réserve plus. Le dire évite de chercher le « + » absent. */}
      {journeePassee && (
        <Typography
          variant="body2"
          sx={{ px: 3, py: 2, color: 'text.secondary', bgcolor: 'action.disabledBackground' }}
        >
          Journée passée : consultation seule, aucun rendez-vous ne peut y être pris.
        </Typography>
      )}
      <Box sx={{ minWidth: 'fit-content' }}>
        {/* ─── Entête : le nom des catégories. */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: grille,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'action.hover',
          }}
        >
          <Box sx={{ px: 3, py: 2 }} />
          {colonnes.map((c) => (
            <Box key={c.id} sx={{ borderLeft: 1, borderColor: 'divider' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, px: 3, pt: 2 }}>
                {c.name}
              </Typography>
              {/* ⭐ Le nom du poste EN TÊTE de sa sous-colonne, là où un agenda
                  de bureau met celui de la personne. C'est ce qui remplace la
                  pastille répétée sur chaque rendez-vous. */}
              <Box sx={{ ...sousColonnes(c), mt: 1 }}>
                {c.postes.map((p, i) => (
                  <Typography
                    key={p.id}
                    variant="caption"
                    noWrap
                    title={p.label}
                    sx={{
                      px: 2,
                      py: 1,
                      color: 'text.secondary',
                      borderLeft: i === 0 ? 0 : 1,
                      borderColor: 'divider',
                    }}
                  >
                    {p.label}
                  </Typography>
                ))}
              </Box>
            </Box>
          ))}
        </Box>

        {/* ─── Une bande par créneau ; dans chaque colonne, une ligne par poste. */}
        {minutes.map((minute, i) => {
          const passe = minute < plancher
          return (
            <Box
              key={minute}
              sx={{
                display: 'grid',
                gridTemplateColumns: grille,
                borderTop: i === 0 ? 0 : 1,
                borderColor: 'divider',
              }}
            >
              <Box
                sx={{
                  px: 3,
                  py: 2,
                  color: passe ? 'text.disabled' : 'text.secondary',
                  bgcolor: passe ? 'action.disabledBackground' : 'transparent',
                  fontVariantNumeric: 'tabular-nums',
                  borderRight: 1,
                  borderColor: 'divider',
                }}
              >
                {enHeure(minute)}
              </Box>

              {colonnes.map((c) => {
                const fermetures = fermeturesParCategorie.get(c.id) ?? []
                const fermes = postesBloques(fermetures, minute, minute + horaires.slotMinutes)

                // La règle vit dans le paquet partagé — la case annonce donc
                // « place fermée », pas « poste 2 fermé » : c'est la seule
                // chose que la boutique a décidée.
                const occupees = new Set(
                  c.postes.filter((p) => occupant(p.id, minute)).map((p) => p.id),
                )
                const closes = placesFermees(
                  c.postes.map((p) => p.id),
                  occupees,
                  fermes,
                )
                const motif =
                  fermetures.find((f) => f.debut < minute + horaires.slotMinutes && f.fin > minute)
                    ?.motif ?? null

                return (
                  <Box
                    key={c.id}
                    sx={{ borderLeft: 1, borderColor: 'divider', ...sousColonnes(c) }}
                  >
                    {c.postes.map((p, iPoste) => {
                      const rdv = occupant(p.id, minute)
                      return (
                        <CasePoste
                          key={p.id}
                          libelle={p.label}
                          rdv={rdv}
                          debute={Boolean(rdv && minutesMurales(rdv.startsAt, fuseau) === minute)}
                          plage={
                            rdv
                              ? `${enHeure(minutesMurales(rdv.startsAt, fuseau))} – ${enHeure(
                                  minutesMurales(rdv.endsAt, fuseau),
                                )}`
                              : undefined
                          }
                          passe={passe}
                          ferme={closes.has(p.id)}
                          motifFermeture={motif}
                          premier={iPoste === 0}
                          onCreneau={() => onCreneau(p.id, c.id, enHeure(minute))}
                          onOuvrir={onOuvrir}
                          onDeplacer={onDeplacer}
                          onAnnuler={onAnnuler}
                          onValider={onValider}
                        />
                      )
                    })}
                  </Box>
                )
              })}
            </Box>
          )
        })}
      </Box>
    </Paper>
  )
}

/**
 * Une case : un poste, à un créneau.
 *
 * ⚠️ Le libellé du poste est répété sur chaque ligne libre. C'est verbeux, et
 * c'est voulu : la case est la cible du clic, et sans son nom on réserverait
 * « la deuxième ligne » sans savoir de quelle place il s'agit.
 */
function CasePoste({
  libelle,
  rdv,
  debute,
  plage,
  passe,
  ferme,
  motifFermeture,
  premier,
  onCreneau,
  onOuvrir,
  onDeplacer,
  onAnnuler,
  onValider,
}: {
  libelle: string
  rdv: RendezVous | undefined
  debute: boolean
  /** « 10:00 – 11:30 », à l'horloge de la boutique. */
  plage?: string | undefined
  passe: boolean
  /** Cette place est FERMÉE à la réservation — un blocage la recouvre. */
  ferme: boolean
  motifFermeture: string | null
  /** Première sous-colonne de la catégorie : elle ne porte pas de séparation. */
  premier: boolean
  onCreneau: () => void
  onOuvrir: (r: RendezVous) => void
  onDeplacer: (r: RendezVous) => void
  onAnnuler: (r: RendezVous) => void
  onValider: (r: RendezVous) => void
}): JSX.Element {
  const continu = rdv !== undefined && !debute
  /**
   * ⭐ VERT pour ce qui vient du site, bleu pour ce qui vient du comptoir.
   *
   * La couleur dit d'un coup d'œil ce qui demande une réponse : un rendez-vous
   * pris en ligne engage la boutique sans qu'elle ait parlé à personne, et il
   * faut qu'elle le voie sans lire chaque ligne.
   */
  const aValider = rdv?.status === 'pending'
  const fondRdv = aValider ? 'success.light' : 'primary.light'

  /**
   * ⚠️ La fermeture ne vaut que sur une case LIBRE. Un rendez-vous déjà posé
   * sur une place qu'on ferme ensuite reste visible et reste cliquable : c'est
   * un engagement pris envers un client, que le planning doit continuer à
   * montrer — à la boutique de l'appeler, pas au logiciel de l'effacer.
   */
  const closeALaReservation = ferme && !rdv

  /**
   * ⭐ Passé et fermé portent la MÊME rayure, et deux libellés distincts.
   *
   * Les deux disent la même chose au comptoir — on ne réserve pas ici — et la
   * grille le signale donc de la même façon. Le gris seul ne suffisait pas :
   * une case vide et claire se lit « libre », et l'on cliquait pour rien.
   *
   * ⚠️ Le PASSÉ l'emporte sur la fermeture. Sur une journée écoulée, savoir
   * qu'une place était par ailleurs fermée n'apprend plus rien ; l'heure, elle,
   * explique tout.
   */
  const indisponible = !rdv && (passe || closeALaReservation)
  const libelleIndispo = passe ? 'Heure passée' : 'Place fermée'

  const corps = (
    <Box
      onClick={() => (rdv ? onOuvrir(rdv) : indisponible ? undefined : onCreneau())}
      sx={{
        px: 2,
        py: 1,
        minHeight: 44,
        display: 'flex',
        // ⚠️ Une case occupée EMPILE ses lignes. La sous-colonne d'un poste est
        // étroite : mis bout à bout, libellé, client et plage s'y repliaient en
        // un bloc illisible.
        flexDirection: rdv ? 'column' : 'row',
        alignItems: rdv ? 'stretch' : 'center',
        // ⚠️ Une case LIBRE centre son « + » ; une case occupée aligne son
        // contenu à gauche, comme un texte se lit.
        justifyContent: rdv ? 'flex-start' : 'center',
        gap: rdv ? 0.25 : 2,
        // ⚠️ Indispensable pour que l'ellipse fonctionne : sans lui, un enfant
        // de flex refuse de rétrécir sous sa largeur de contenu et déborde.
        minWidth: 0,
        borderLeft: premier ? 0 : 1,
        cursor: rdv || !indisponible ? 'pointer' : 'not-allowed',
        bgcolor: rdv ? fondRdv : indisponible ? 'action.disabledBackground' : 'transparent',
        // ⭐ HACHURÉE, et pas seulement grise : une case claire et vide se lit
        // « libre ». La rayure dit « on ne réserve pas ici » ; le libellé dit
        // pourquoi.
        ...(indisponible && {
          backgroundImage: (t: { palette: { action: { disabled: string } } }) =>
            `repeating-linear-gradient(45deg, transparent 0 5px, ${t.palette.action.disabled} 5px 6px)`,
        }),
        // Un rendez-vous passé reste LISIBLE — c'est l'historique — mais
        // s'efface un peu devant ceux qui restent à venir.
        opacity: rdv?.status === 'cancelled' ? 0.5 : rdv && passe ? 0.7 : 1,
        borderColor: 'divider',
        /**
         * ⭐ Un rendez-vous qui déborde se SOUDE à sa case du dessus.
         *
         * La séparation entre deux bandes est tracée par la bande elle-même,
         * sur toute la largeur. Maintenant que chaque poste tient sa colonne,
         * ses cases se touchent : la case de continuation remonte d'un pixel
         * pour recouvrir le trait, et le rendez-vous se lit d'un seul bloc,
         * haut comme sa durée.
         */
        ...(continu && { mt: '-1px' }),
        '&:hover': {
          bgcolor: rdv ? fondRdv : indisponible ? 'action.disabledBackground' : 'action.hover',
        },
      }}
    >
      {rdv && debute && (
        <>
          {/* ⚠️ Tronqués, jamais repliés : la sous-colonne d'un poste est
              étroite, et un libellé sur trois lignes ferait respirer la bande
              au point de décaler toute la journée. Le titre rend le texte
              entier au survol. */}
          <Typography variant="body2" noWrap title={rdv.label} sx={{ fontWeight: 600 }}>
            {rdv.label}
          </Typography>
          <Typography
            variant="body2"
            noWrap
            title={rdv.customerName ?? 'Client'}
            sx={{ color: 'text.secondary' }}
          >
            {rdv.customerName ?? 'Client'}
          </Typography>
          {/* ⭐ La PLAGE, pas seulement l'heure de début. Un rendez-vous d'une
              heure et demie traverse trois bandes : sans elle, savoir quand il
              finit demandait de compter les lignes sous le bloc. */}
          <Typography variant="caption" className="tabular" sx={{ color: 'text.secondary' }}>
            {plage}
          </Typography>
          {rdv.transactionRef && (
            <Chip size="small" variant="outlined" label={rdv.transactionRef} />
          )}
          {/* ⚠️ La pastille « À valider » est CLIQUABLE : c'est le geste
              qu'on vient faire en voyant le vert, et l'enfouir dans un menu
              aurait rendu la validation plus coûteuse que le rendez-vous. */}
          {aValider ? (
            <Chip
              size="small"
              color="success"
              label={LIBELLES_STATUT_RDV.pending}
              onClick={(e) => {
                e.stopPropagation()
                onValider(rdv)
              }}
            />
          ) : (
            rdv.status !== 'booked' && <Chip size="small" label={LIBELLES_STATUT_RDV[rdv.status]} />
          )}

          {/* ⭐ Les deux gestes du comptoir, à portée de main sur la ligne :
              déplacer et annuler. Les enfouir dans la fiche de vente aurait
              obligé à quitter le planning — or c'est en le regardant, client au
              téléphone, qu'on les fait.
              ⚠️ Ni l'un ni l'autre sur un rendez-vous déjà annulé : il n'y a
              plus rien à libérer ni à déplacer. */}
          {rdv.status !== 'cancelled' && (
            <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
              <Tooltip title="Déplacer">
                <IconButton
                  size="small"
                  aria-label={`Déplacer le rendez-vous de ${rdv.customerName ?? 'client'}`}
                  onClick={(e) => {
                    // ⚠️ Sans cet arrêt, le clic remonte à la case et ouvre la
                    // vente : on partirait de l'agenda au moment précis où
                    // l'on veut y rester.
                    e.stopPropagation()
                    onDeplacer(rdv)
                  }}
                >
                  <SwapHorizIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Annuler">
                <IconButton
                  size="small"
                  aria-label={`Annuler le rendez-vous de ${rdv.customerName ?? 'client'}`}
                  onClick={(e) => {
                    e.stopPropagation()
                    onAnnuler(rdv)
                  }}
                >
                  <EventBusyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </>
      )}

      {/* ⭐ Un « + » simple et centré, sans libellé : la case EST le bouton.
          Écrire « Poste 2 · libre » répétait sur chaque ligne ce que la colonne
          dit déjà, et n'indiquait pas qu'on pouvait cliquer.
          ⚠️ Rien sur une heure passée — proposer d'y poser un rendez-vous
          serait une promesse que la prise refuserait ensuite. */}
      {!rdv && !indisponible && (
        <Box
          component="span"
          role="img"
          aria-label={`Prendre rendez-vous — poste ${libelle}`}
          sx={{ fontSize: 14, lineHeight: 1, color: 'text.primary' }}
        >
          +
        </Box>
      )}

      {/* ⚠️ « Place fermée », et non « poste fermé » : la boutique a décidé
          d'un NOMBRE de places, pas d'un fauteuil. Écrire le nom du poste
          aurait inventé une décision que personne n'a prise. */}
      {indisponible && (
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1 }}
        >
          {passe ? <HistoryIcon sx={{ fontSize: 14 }} /> : <BlockIcon sx={{ fontSize: 14 }} />}
          {libelleIndispo}
        </Typography>
      )}
    </Box>
  )

  /**
   * ⭐ Le MOTIF en infobulle, pas dans la case. « Formation », « congé »,
   * « travaux » est la première question posée devant un client qui demande
   * cette heure-là — mais l'écrire sur chaque ligne aurait noyé la grille sous
   * un texte répété à chaque créneau de la période.
   *
   * ⚠️ Rien de tel pour une heure passée : le libellé dit déjà tout, et une
   * infobulle sur chaque case d'une journée écoulée se déclencherait au moindre
   * survol de la grille qu'on relit.
   */
  if (closeALaReservation && !passe) {
    return <Tooltip title={motifFermeture ?? 'Place fermée à la réservation'}>{corps}</Tooltip>
  }

  return corps
}
// ───────────────────────────────────────────────────────────────────── le mois

function VueMois({
  curseur,
  aujourdhui,
  parJour,
  onJour,
}: {
  curseur: Date
  /** Le jour civil qu'il est à la BOUTIQUE. */
  aujourdhui: string
  parJour: Map<string, RendezVous[]>
  onJour: (j: Date) => void
}): JSX.Element {
  const premier = new Date(curseur.getFullYear(), curseur.getMonth(), 1)
  const cases: (Date | null)[] = [
    ...Array<null>(colonneLundiEnTete(premier)).fill(null),
    ...Array.from(
      { length: new Date(curseur.getFullYear(), curseur.getMonth() + 1, 0).getDate() },
      (_, i) => new Date(curseur.getFullYear(), curseur.getMonth(), i + 1),
    ),
  ]

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
        {ENTETES_SEMAINE.map((j, i) => (
          <Typography
            key={i}
            variant="caption"
            sx={{ textAlign: 'center', color: 'text.secondary', pb: 1 }}
          >
            {j}
          </Typography>
        ))}

        {cases.map((jour, i) => {
          if (!jour) return <Box key={`vide-${i}`} />
          const dedans = parJour.get(jour.toDateString()) ?? []
          // ⚠️ « Aujourd'hui » est celui de la BOUTIQUE, pas du poste : le soir
          // à Paris, c'est encore la veille à Bamako.
          const civil = versIso(jour)
          const estAujourdhui = civil === aujourdhui
          // Un jour passé reste CLIQUABLE — on y consulte qui est venu — mais se
          // grise : on n'y réserve plus rien.
          const passe = civil < aujourdhui
          return (
            <Box
              key={jour.toISOString()}
              onClick={() => onJour(jour)}
              title={passe ? 'Jour passé : consultation seule' : undefined}
              sx={{
                minHeight: 78,
                p: 2,
                borderRadius: 2,
                border: 1,
                borderColor: estAujourdhui ? 'primary.main' : 'divider',
                bgcolor: passe ? 'action.disabledBackground' : 'transparent',
                cursor: 'pointer',
                '&:hover': { bgcolor: passe ? 'action.disabledBackground' : 'action.hover' },
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: estAujourdhui ? 700 : 400,
                  color: passe ? 'text.disabled' : 'text.primary',
                }}
              >
                {jour.getDate()}
              </Typography>
              {/* Des pastilles plutôt que des libellés : à cette échelle, on
                  cherche « chargé ou pas », pas « qui vient ». */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '3px', mt: 1 }}>
                {dedans.slice(0, 6).map((r) => (
                  <Box
                    key={r.id}
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      bgcolor: passe ? 'text.disabled' : 'primary.main',
                    }}
                  />
                ))}
                {dedans.length > 6 && (
                  <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1 }}>
                    +{dedans.length - 6}
                  </Typography>
                )}
              </Box>
            </Box>
          )
        })}
      </Box>
    </Paper>
  )
}

// ──────────────────────────────────────────────────────────────────── l'année

function VueAnnee({
  annee,
  aujourdhui,
  parJour,
  onMois,
}: {
  annee: number
  /** Le jour civil qu'il est à la BOUTIQUE. */
  aujourdhui: string
  parJour: Map<string, RendezVous[]>
  onMois: (m: number) => void
}): JSX.Element {
  /** Le mois le plus chargé donne l'échelle : une teinte relative se lit mieux. */
  const parMois = Array.from({ length: 12 }, (_, m) => {
    let total = 0
    for (const [cle, liste] of parJour) {
      const d = new Date(cle)
      if (d.getFullYear() === annee && d.getMonth() === m) total += liste.length
    }
    return total
  })
  const sommet = Math.max(1, ...parMois)

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' },
        gap: 3,
      }}
    >
      {MOIS_FR.map((nom, m) => {
        // Un mois ENTIÈREMENT écoulé se grise ; le mois en cours, non — il lui
        // reste des jours.
        const passe = `${annee}-${String(m + 1).padStart(2, '0')}` < aujourdhui.slice(0, 7)
        return (
          <Paper
            key={nom}
            variant="outlined"
            onClick={() => onMois(m)}
            title={passe ? 'Mois passé : consultation seule' : undefined}
            sx={{
              p: 3,
              cursor: 'pointer',
              bgcolor: passe ? 'action.disabledBackground' : 'background.paper',
              '&:hover': { borderColor: passe ? 'divider' : 'primary.main' },
            }}
          >
            <Typography
              variant="body2"
              sx={{
                textTransform: 'capitalize',
                fontWeight: 600,
                color: passe ? 'text.disabled' : 'text.primary',
              }}
            >
              {nom}
            </Typography>
            <Typography variant="h2" sx={{ mt: 1 }}>
              {parMois[m]}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {parMois[m] === 0
                ? 'aucun rendez-vous'
                : parMois[m] === 1
                  ? 'rendez-vous'
                  : 'rendez-vous'}
            </Typography>
            {/* Une barre relative au mois le plus chargé : la saison se voit. */}
            <Box sx={{ height: 6, borderRadius: 3, bgcolor: 'action.hover', mt: 2 }}>
              <Box
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  width: `${((parMois[m] ?? 0) / sommet) * 100}%`,
                  bgcolor: passe ? 'text.disabled' : 'primary.main',
                }}
              />
            </Box>
          </Paper>
        )
      })}
    </Box>
  )
}
